// frontend/src/pages/Transfers/EditTransferform.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { 
  ArrowLeftRight, Building, Save, Loader2, AlertCircle, 
  X, Calendar, FileText, DollarSign, TrendingDown, TrendingUp,
  RefreshCw, User, CheckCircle, Send
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import "./Transferform.css";

const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Robust helper to extract a 3‑letter currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const EditTransferform = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency, getCurrencySymbol } = useCurrency();

  // ✅ Get company currency safely
  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  // ✅ Format amount with company currency
  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return formatCurrency(0, companyCurrency);
    return formatCurrency(amount, companyCurrency);
  };

  const [formData, setFormData] = useState({
    fromaccount_id: "",
    toaccount_id: "",
    transfer_type: "OUT",
    amount: "",
    description: "",
    transaction_date: "",
    status: "active",
  });

  const [transferDoc, setTransferDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  const fetchAccounts = async (companyId) => {
    if (!companyId) return;
  
    try {
      setLoadingAccounts(true);
  
      let endpoint =
        `${url}/api/account/dropdown?include_inactive=false&type=Asset&posting_only=true&transfer_only=true`;
  
      endpoint += `&company_id=${companyId}`;
  
      const response = await axios.get(endpoint);
  
      if (response.data.success) {
        setAccounts(response.data.data || []);        
      }  
    } catch (error) {
      toast.error("Failed to load transfer accounts");
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchTransfer = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/api/transferform/${id}`);
      
      if (res.data.success) {
        setTransferDoc(res.data.data);
        
        setFormData({
          fromaccount_id:
            res.data.data.fromaccount_id?._id ||
            res.data.data.fromaccount_id ||
            "",
        
          toaccount_id:
            res.data.data.toaccount_id?._id ||
            res.data.data.toaccount_id ||
            "",
        
          transfer_type:
            res.data.data.transfer_type || "OUT",
        
          amount:
            res.data.data.amount || "",
        
          description:
            res.data.data.description || "",
        
          transaction_date:
            res.data.data.transaction_date?.split("T")[0] || "",
        
          status:
            res.data.data.status || "active",
        });
        
        // Fetch accounts for the company
        const companyId = res.data.data.company_id?._id || res.data.data.company_id;
        if (companyId) {
          await fetchAccounts(companyId);
        }
      } else {
        toast.error(res.data.message || "Failed to load transfer");
        navigate("/ViewTransfers");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load transfer");
      navigate("/ViewTransfers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransfer();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleFocus = (field) => setFocused({ ...focused, [field]: true });
  const handleBlur = (field) => {
    setFocused({ 
      ...focused, 
      [field]: formData[field] && formData[field].toString().length > 0 
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fromaccount_id) {
      newErrors.fromaccount_id = "From account is required";
    }
    
    if (!formData.toaccount_id) {
      newErrors.toaccount_id = "To account is required";
    }
    
    if (formData.fromaccount_id && formData.toaccount_id && 
        formData.fromaccount_id === formData.toaccount_id) {
      newErrors.toaccount_id = "From and To accounts cannot be the same";
    }
    
    if (!formData.amount) {
      newErrors.amount = "Amount is required";
    } else if (parseFloat(formData.amount) <= 0) {
      newErrors.amount = "Amount must be greater than 0";
    }
    
    if (!formData.transaction_date) {
      newErrors.transaction_date = "Transaction date is required";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    
    setErrors({});
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }

    setSubmitting(true);
    try {
      const updateData = {
        fromaccount_id: formData.fromaccount_id,
        toaccount_id: formData.toaccount_id,
        transfer_type: formData.transfer_type || "OUT",
        amount: Number(formData.amount),
        description: formData.description ? formData.description.trim() : "",
        transaction_date: formData.transaction_date,
        status: formData.status,
      };
      
      const res = await axios.put(`${url}/api/transferform/${id}`, updateData);
      
      if (res.data.success) {
        toast.success("Transfer updated successfully!");
        
        setTimeout(() => {
          navigate("/ViewTransfers");
        }, 1000);
      } else {
        toast.error(res.data.message || "Update failed");
        setSubmitting(false);
      }
    } catch (err) {     
      if (err.response) {
        toast.error(err.response.data?.message || "Update failed");
        
        if (err.response.status === 409) {
          setErrors(prev => ({
            ...prev,
            fromaccount_id: "Invalid account combination"
          }));
        }
        
        if (err.response.data?.errors) {
          const newErrors = {};
          if (typeof err.response.data.errors === 'object') {
            Object.keys(err.response.data.errors).forEach(key => {
              newErrors[key] = err.response.data.errors[key];
            });
          }
          setErrors(prev => ({ ...prev, ...newErrors }));
        }
      } else if (err.request) {
        toast.error("No response from server. Please check your connection.");
      } else {
        toast.error("Error: " + err.message);
      }
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/ViewTransfers");
  };

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="transfer-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading transfer details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!transferDoc) {
    return (
      <div className="form-wrapper">
        <div className="transfer-form-container">
          <div className="error-container">
            <AlertCircle size={32} color="#dc3545" />
            <p>Transfer not found</p>
            <button 
              className="cancel-btn" 
              onClick={() => navigate("/ViewTransfers")}
            >
              Back to List
            </button>
          </div>
        </div>
      </div>
    );
  }

  const canEdit = formData.status === "active";

  return (
    <div className="form-wrapper">
      <div className="transfer-form-container">
        <button
          className="close-btn"
          onClick={handleCancel}
          title="Go Back"
          disabled={submitting}
        >
          <X size={20} />
        </button>

        <div className="form-header">
          <ArrowLeftRight size={24} />
          <h2 className="form-title">Edit Transfer #{transferDoc.transfer_id}</h2>
          <p className="form-subtitle">
            Update transfer details
          </p>
        </div>

        <form className="transfer-form" onSubmit={handleSubmit}>
          {/* From Account */}
          <div className={`form-field required ${focused.fromaccount_id || formData.fromaccount_id ? "filled" : ""} ${errors.fromaccount_id ? 'error' : ''}`}>
            <div className="select-wrapper">
              <select
                name="fromaccount_id"
                value={formData.fromaccount_id}
                onChange={handleChange}
                onFocus={() => handleFocus("fromaccount_id")}
                onBlur={() => handleBlur("fromaccount_id")}
                required
                disabled={!canEdit || submitting || loadingAccounts}
              >
                <option value="">Select From Account</option>
                {accounts.map((account) => (
                  <option key={account._id} value={account._id}>
                    {account.account_code} — {account.account_name} - Balance: {formatAmount(account.balance)}
                  </option>
                ))}
              </select>
              <TrendingDown size={16} className="select-icon" />
            </div>
            <label>From Account *</label>
            {errors.fromaccount_id && <small className="field-error">{errors.fromaccount_id}</small>}
          </div>

          {/* To Account */}
          <div className={`form-field required ${focused.toaccount_id || formData.toaccount_id ? "filled" : ""} ${errors.toaccount_id ? 'error' : ''}`}>
            <div className="select-wrapper">
              <select
                name="toaccount_id"
                value={formData.toaccount_id}
                onChange={handleChange}
                onFocus={() => handleFocus("toaccount_id")}
                onBlur={() => handleBlur("toaccount_id")}
                required
                disabled={!canEdit || submitting || loadingAccounts}
              >
                <option value="">Select To Account</option>
                {accounts.map((account) => (
                  <option key={account._id} value={account._id}>
                    {account.account_code} — {account.account_name} - Balance: {formatAmount(account.balance)}
                  </option>
                ))}
              </select>
              <TrendingUp size={16} className="select-icon" />
            </div>
            <label>To Account *</label>
            {errors.toaccount_id && <small className="field-error">{errors.toaccount_id}</small>}
          </div>

          {/* Amount */}
          <div className={`form-field required ${focused.amount || formData.amount ? "filled" : ""} ${errors.amount ? 'error' : ''}`}>
            <input
              type="number"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              onFocus={() => handleFocus("amount")}
              onBlur={() => handleBlur("amount")}
              placeholder="Enter amount"
              min="0.01"
              step="0.01"
              required
              disabled={!canEdit || submitting}
            />
            <label>
              <DollarSign size={14} className="mr-2" />
              Amount *
            </label>
            {errors.amount && <small className="field-error">{errors.amount}</small>}
          </div>

          {/* Transaction Date */}
          <div className={`form-field required ${focused.transaction_date || formData.transaction_date ? "filled" : ""} ${errors.transaction_date ? 'error' : ''}`}>
            <input
              type="date"
              name="transaction_date"
              value={formData.transaction_date}
              onChange={handleChange}
              onFocus={() => handleFocus("transaction_date")}
              onBlur={() => handleBlur("transaction_date")}
              required
              disabled={!canEdit || submitting}
            />
            <label>
              <Calendar size={14} className="mr-2" />
              Transaction Date *
            </label>
            {errors.transaction_date && <small className="field-error">{errors.transaction_date}</small>}
          </div>

          {/* Description */}
          <div className={`form-field ${focused.description || formData.description ? "filled" : ""} ${errors.description ? 'error' : ''}`}>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              onFocus={() => handleFocus("description")}
              onBlur={() => handleBlur("description")}
              placeholder="Enter description (optional)"
              rows="3"
              disabled={!canEdit || submitting}
            />
            <label>
              <FileText size={14} className="mr-2" />
              Description
            </label>
            {errors.description && <small className="field-error">{errors.description}</small>}
          </div>

          {/* Status */}
          <div className="form-field">
            <div className="select-wrapper">
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                disabled={submitting}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <AlertCircle size={16} className="select-icon" />
            </div>
            <label>Status</label>
            <small className="field-hint">
              {formData.status === 'active' 
                ? "Active transfers are visible in reports"
                : "Inactive transfers are hidden from reports"
              }
            </small>
          </div>

          {/* Warning if editing inactive transfer */}
          {formData.status === 'inactive' && (
            <div className="warning-message">
              <AlertCircle size={16} />
              <span>Inactive transfers cannot be edited. Change status to active to make changes.</span>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || (!canEdit && formData.status === 'inactive') || loadingAccounts}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Updating...
                </>
              ) : (
                <>
                  Update Transfer
                  <Send className="ml-2" size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTransferform;